import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from "@mui/material";
import { IoSettingsOutline } from "react-icons/io5";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import ImportButtons from "./ImportButtons";
import { themeColors } from "../../utils/theme";
import { OutreachCategory } from "../../types/outreach";

interface OutreachSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  refreshingYouTube: boolean;
  refreshingSpotify: boolean;
  refreshingMusicBlogs: boolean;
  onRefreshYouTube: () => void;
  onRefreshSpotify: () => void;
  onRefreshMusicBlogs: () => void;
  columns?: GridColDef[];
  columnVisibility?: GridColumnVisibilityModel;
  onColumnVisibilityChange?: (model: GridColumnVisibilityModel) => void;
  activeTab: number;
  onImportComplete: () => void;
}

export const OutreachSettingsDrawer: React.FC<OutreachSettingsDrawerProps> = ({
  open,
  onClose,
  refreshingYouTube,
  refreshingSpotify,
  refreshingMusicBlogs,
  onRefreshYouTube,
  onRefreshSpotify,
  onRefreshMusicBlogs,
  columns = [],
  columnVisibility = {},
  onColumnVisibilityChange,
  activeTab,
  onImportComplete,
}) => {
  // Handle column visibility change
  const handleColumnVisibilityChange = (field: string, isVisible: boolean) => {
    if (onColumnVisibilityChange) {
      const newModel = { ...columnVisibility, [field]: isVisible };
      onColumnVisibilityChange(newModel);
    }
  };

  // Check if the current tab is for campaigns
  const isCampaignsTab = (): boolean => {
    return activeTab === 4; // Assuming campaigns is tab index 4
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: 320,
          boxSizing: "border-box",
          p: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">Settings</Typography>
        <IconButton onClick={onClose}>
          <IoSettingsOutline />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Column Visibility Controls */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Column Visibility</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup>
            {columns.map(
              (column) =>
                // Skip the actions column as it should always be visible
                column.field !== "actions" && (
                  <FormControlLabel
                    key={column.field}
                    control={
                      <Checkbox
                        checked={columnVisibility[column.field] !== false}
                        onChange={(e) =>
                          handleColumnVisibilityChange(
                            column.field,
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={column.headerName}
                  />
                )
            )}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      {/* Import Buttons */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Import Data</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <ImportButtons
              activeTab={activeTab}
              onImportComplete={onImportComplete}
              vertical={true}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Analytics Refresh - Hide for Campaigns tab */}
      {!isCampaignsTab() && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Analytics Refresh</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={
                  refreshingYouTube ? (
                    <Skeleton 
                      variant="circular" 
                      width={20} 
                      height={20} 
                      sx={{
                        bgcolor: `${themeColors.accent}40`,
                        '&::after': {
                          background: `linear-gradient(90deg, transparent, ${themeColors.primary}20, transparent)`,
                        },
                      }}
                    />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={onRefreshYouTube}
                disabled={refreshingYouTube}
                fullWidth
                sx={{ mb: 1 }}
              >
                Refresh YouTube Analytics
              </Button>

              <Button
                variant="outlined"
                startIcon={
                  refreshingSpotify ? (
                    <Skeleton 
                      variant="circular" 
                      width={20} 
                      height={20} 
                      sx={{
                        bgcolor: `${themeColors.accent}40`,
                        '&::after': {
                          background: `linear-gradient(90deg, transparent, ${themeColors.primary}20, transparent)`,
                        },
                      }}
                    />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={onRefreshSpotify}
                disabled={refreshingSpotify}
                fullWidth
                sx={{ mb: 1 }}
              >
                Refresh Spotify Analytics
              </Button>

              <Button
                variant="outlined"
                startIcon={
                  refreshingMusicBlogs ? (
                    <Skeleton 
                      variant="circular" 
                      width={20} 
                      height={20} 
                      sx={{
                        bgcolor: `${themeColors.accent}40`,
                        '&::after': {
                          background: `linear-gradient(90deg, transparent, ${themeColors.primary}20, transparent)`,
                        },
                      }}
                    />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={onRefreshMusicBlogs}
                disabled={refreshingMusicBlogs}
                fullWidth
              >
                Refresh Music Blogs Analytics
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Drawer>
  );
};
